import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  GlobalStyles,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  type SvgIconProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useId, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { InstallAppCard } from '../components/InstallAppCard';
import { OfflineIndicator } from '../components/OfflineIndicator';
import { useWorkerNeedsYou } from '../lib/useWorkerNeedsYou';
import { signOutCurrent } from '../services/firebase';
import { useMyConversations } from '../services/useMessaging';
import { color } from '../theme/foundation';

import { LanguageSwitcherMui } from './LanguageSwitcherMui';
import { MessagesMenu } from './MessagesMenu';
import { NotificationBell } from './NotificationBell';
import { PageHeaderProvider, useShellHeading } from './pageHeaderContext';
import {
  shellActionsSpacing,
  shellDrawerPaperSx,
  shellHeaderSx,
  shellMainSx,
  shellRootSx,
  shellTitleSx,
} from './shellStyles';

/**
 * UX 1A-e — the worker's navigation (UI-alus §1 "Navigatsioon", §3).
 *
 *  - < 600 px: bottom bar Täna · Päevad · Sõnumid · Puudumine · Veel; "Veel"
 *    (and the header menu button) open the drawer with everything, Tööd and
 *    Profiil included.
 *  - 600–1199 px: a rail — icon over a one-word label — with every destination.
 *  - ≥ 1200 px: the permanent side menu.
 *
 * Exactly one menu landmark ("Menüü") exists at any width: only the drawer or
 * rail for the current width is mounted. The Päevad badge is the "Vajab sind"
 * count (`workerNeedsYou`) — the one critical badge besides unread messages.
 */

const DRAWER_WIDTH = 248;
const RAIL_WIDTH = 88;

type NavKey = 'today' | 'days' | 'messages' | 'absence' | 'jobs' | 'profile';

const NAV_ITEMS: ReadonlyArray<{
  to: string;
  key: NavKey;
  Icon: ComponentType<SvgIconProps>;
}> = [
  { to: '/worker', key: 'today', Icon: TodayOutlinedIcon },
  { to: '/worker/timesheets', key: 'days', Icon: AssignmentTurnedInOutlinedIcon },
  { to: '/worker/messages', key: 'messages', Icon: ChatBubbleOutlineOutlinedIcon },
  { to: '/worker/leave', key: 'absence', Icon: BeachAccessOutlinedIcon },
  { to: '/worker/jobs', key: 'jobs', Icon: WorkOutlineOutlinedIcon },
  { to: '/worker/profile', key: 'profile', Icon: PersonOutlineOutlinedIcon },
];

/** On the phone's bottom bar; the rest are under "Veel". */
const BAR_KEYS: ReadonlyArray<NavKey> = ['today', 'days', 'messages', 'absence'];

/** The shell label (the page's h1 unless a PageHeader owns it). */
const TITLE_KEY: Record<NavKey, string> = {
  today: 'worker:nav.today',
  days: 'worker:nav.days',
  messages: 'worker:pageTitles.messages',
  absence: 'worker:nav.absence',
  jobs: 'worker:pageTitles.jobs',
  profile: 'worker:pageTitles.profile',
};

function getPageKey(pathname: string): NavKey {
  if (pathname === '/worker' || pathname === '/worker/') return 'today';
  const match = NAV_ITEMS.find((item) => item.to !== '/worker' && pathname.startsWith(item.to));
  return match?.key ?? 'today';
}

const srOnly = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * The page title in the header: the page's `h1` unless a `PageHeader` on the
 * route owns it (UX 1.0a contract, pageHeaderContext.tsx). Never an `h2`.
 * A component of its own because the hook must run inside the provider.
 */
function ShellTitle({ label }: { label: string }) {
  const heading = useShellHeading(label);
  return (
    <Typography
      variant="h2"
      noWrap
      {...heading.titleProps}
      sx={[shellTitleSx, heading.titleProps.sx] as SxProps<Theme>}
    >
      {label}
    </Typography>
  );
}

function barButtonSx(active: boolean): SxProps<Theme> {
  return {
    minWidth: 0,
    minHeight: 64,
    px: 0.5,
    borderRadius: 0,
    flexDirection: 'column',
    gap: 0.5,
    textTransform: 'none',
    fontSize: 12,
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
    // F1: brandText on brandSoft (6.15:1). primary.main on MUI's
    // grey action.selected was 4.29:1 — under the 4.5 text minimum.
    color: active ? color.brandText : 'text.secondary',
    bgcolor: active ? color.brandSoft : undefined,
  };
}

export function WorkerShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['worker', 'common']);
  const { user } = useAuth();
  const location = useLocation();
  // `noSsr`: read the width on the first render, so the menu for the wrong
  // width (and its nav landmark) never mounts for a frame.
  const tablet = useMediaQuery('(min-width:600px)', { noSsr: true });
  const wide = useMediaQuery('(min-width:1200px)', { noSsr: true });
  const phone = !tablet;
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerId = useId();
  // UX step 0 — from the participant QUERY, not a single-document listener.
  // Before RM24's first message the worker's thread does not exist, the rule
  // refuses a `get` on a missing document, and that listener died for good:
  // the first message never lit the badge until a reload. A query on
  // `participantUids array-contains uid` is allowed when it matches nothing
  // and keeps listening, so the thread appears the moment it is created.
  const { conversations: myConversations } = useMyConversations(user?.uid);
  const messagesUnread = user?.uid
    ? myConversations.reduce((n, c) => n + Math.max(0, c.unreadBy?.[user.uid] ?? 0), 0)
    : 0;
  const needsYou = useWorkerNeedsYou().length;

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // The drawer belongs to the phone layout; widening the window closes it.
  useEffect(() => {
    if (!phone) setMobileOpen(false);
  }, [phone]);

  const activeKey = getPageKey(location.pathname);
  const displayName = user?.displayName ?? user?.email ?? '';
  const initials = displayName
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const label = (key: NavKey | 'more') => t(`worker:nav.${key}`);

  /** The icon with its badge. The needs-you count is hidden from AT; `countText` names it. */
  const navIcon = (key: NavKey, Icon: ComponentType<SvgIconProps>, small = false) => {
    const icon = <Icon fontSize={small ? 'small' : 'medium'} />;
    if (key === 'messages' && messagesUnread > 0) {
      return (
        <Badge badgeContent={messagesUnread} color="error">
          {icon}
        </Badge>
      );
    }
    if (key === 'days' && needsYou > 0) {
      return (
        <Badge
          badgeContent={needsYou}
          color="error"
          data-testid="needs-you-badge"
          slotProps={{ badge: { 'aria-hidden': true } as Record<string, unknown> }}
        >
          {icon}
        </Badge>
      );
    }
    return icon;
  };
  const countText = (key: NavKey) =>
    key === 'days' && needsYou > 0 ? (
      <Box component="span" sx={srOnly}>
        {` (${t('worker:nav.needsYouCount', { count: needsYou })})`}
      </Box>
    ) : null;

  const menuName = t('common:ui.shell.navLabel', { defaultValue: 'Menüü' });

  const sidebar = (
    <Box
      component="nav"
      id={drawerId}
      aria-label={menuName}
      sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/rm24_logo.jpg"
          alt={t('common:app.name')}
          sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: 'cover' }}
        />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 }}
          >
            {t('common:app.name')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('worker:shell.portalLabel')}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mx: 1 }} />
      <List sx={{ py: 1, flexGrow: 1 }}>
        {/* Each link in its own <li>: a link directly inside <ul> breaks the list (axe "list"). */}
        {NAV_ITEMS.map(({ to, key, Icon }) => (
          <ListItem key={to} disablePadding>
            <ListItemButton
              component={NavLink}
              to={to}
              end={to === '/worker'}
              selected={activeKey === key}
              onClick={() => setMobileOpen(false)}
              sx={{ minHeight: 48 }}
            >
              <ListItemIcon>{navIcon(key, Icon, true)}</ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {label(key)}
                    {countText(key)}
                  </>
                }
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: activeKey === key ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mx: 1 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton onClick={() => void signOutCurrent()} sx={{ minHeight: 48 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('common:actions.signOut')}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  /** 600–1199 px: M3 navigation rail — every destination, icon over a one-word label. */
  const rail = (
    <Box
      component="nav"
      aria-label={menuName}
      sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: RAIL_WIDTH,
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        pt: 'calc(12px + env(safe-area-inset-top, 0px))',
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        pl: 'env(safe-area-inset-left, 0px)',
        overflowY: 'auto',
      }}
    >
      <Box
        component="img"
        src="/rm24_logo.jpg"
        alt={t('common:app.name')}
        sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'cover', mx: 'auto', mb: 1.5 }}
      />
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, flexGrow: 1 }}>
        {NAV_ITEMS.map(({ to, key, Icon }) => (
          <Box component="li" key={to} sx={{ px: 0.5, py: 0.25 }}>
            <ButtonBase
              component={NavLink}
              to={to}
              end={to === '/worker'}
              sx={{
                width: '100%',
                minHeight: 56,
                py: 0.75,
                borderRadius: 2,
                flexDirection: 'column',
                gap: 0.5,
                fontSize: 12,
                fontWeight: activeKey === key ? 700 : 500,
                lineHeight: 1.2,
                textAlign: 'center',
                overflowWrap: 'anywhere',
                color: activeKey === key ? color.brandText : 'text.secondary',
                bgcolor: activeKey === key ? color.brandSoft : undefined,
              }}
            >
              {navIcon(key, Icon)}
              <span>
                {label(key)}
                {countText(key)}
              </span>
            </ButtonBase>
          </Box>
        ))}
      </Box>
      <ButtonBase
        onClick={() => void signOutCurrent()}
        sx={{
          mx: 0.5,
          minHeight: 56,
          borderRadius: 2,
          flexDirection: 'column',
          gap: 0.5,
          fontSize: 12,
          lineHeight: 1.2,
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        <LogoutIcon />
        {t('common:actions.signOut')}
      </ButtonBase>
    </Box>
  );

  const drawerPaperSx = {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      ...shellDrawerPaperSx,
    },
  } as const;

  return (
    <Box sx={shellRootSx}>
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--worker-navigation-offset': 'calc(80px + env(safe-area-inset-bottom, 0px))',
            '--rm24-bottom-nav-height': 'calc(65px + env(safe-area-inset-bottom, 0px))',
            [theme.breakpoints.up('sm')]: {
              '--worker-navigation-offset': '0px',
              '--rm24-bottom-nav-height': '0px',
            },
          },
        })}
      />
      {/* Only the menu for the current width is mounted, so exactly one menu
          landmark exists. The slot reserves the rail's / side menu's width. */}
      <Box sx={{ width: wide ? DRAWER_WIDTH : tablet ? RAIL_WIDTH : 0, flexShrink: 0 }}>
        {wide ? (
          <Drawer variant="permanent" open sx={drawerPaperSx}>
            {sidebar}
          </Drawer>
        ) : tablet ? (
          rail
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              // Kept mounted for speed, but a CLOSED modal drawer must not take
              // part in layout: MUI only hides it with `visibility`, so its box
              // stayed and stretched the document to 540px on a 393px phone.
              '&.MuiModal-hidden': { display: 'none' },
              ...drawerPaperSx,
            }}
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeaderProvider>
          <Toolbar component="header" disableGutters sx={shellHeaderSx}>
            {phone && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen((v) => !v)}
                sx={{ mr: 1.5 }}
                aria-label={t('worker:mobileNavigation.menu')}
                aria-expanded={mobileOpen}
                aria-controls={drawerId}
              >
                <MenuIcon />
              </IconButton>
            )}

            <ShellTitle label={t(TITLE_KEY[activeKey])} />

            <Stack direction="row" spacing={shellActionsSpacing} alignItems="center">
              <LanguageSwitcherMui />
              {wide && <MessagesMenu variant="worker" />}
              <NotificationBell />
              {wide && (
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pl: 1 }}>
                  {/* F1: the orange fill with dark initials (6.93:1). */}
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: color.brandFill,
                      color: color.onBrandFill,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {initials || 'W'}
                  </Avatar>
                  <Box sx={{ lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('worker:header.workerRole')}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Toolbar>

          <Box
            component="main"
            sx={[
              ...(Array.isArray(shellMainSx) ? shellMainSx : [shellMainSx]),
              {
                pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 3 },
                '& .MuiSnackbar-anchorOriginBottomCenter': {
                  bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))', sm: 24 },
                },
              },
            ]}
          >
            <OfflineIndicator />
            {children}
            <Box sx={{ mt: 2 }}>
              <InstallAppCard />
            </Box>
          </Box>
        </PageHeaderProvider>
      </Box>
      {phone && (
        <Box
          component="nav"
          aria-label={t('worker:mobileNavigation.title')}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            pb: 'env(safe-area-inset-bottom, 0px)',
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
          }}
        >
          {NAV_ITEMS.filter(({ key }) => BAR_KEYS.includes(key)).map(({ to, key, Icon }) => (
            <Button
              key={key}
              component={NavLink}
              to={to}
              end={to === '/worker'}
              sx={barButtonSx(activeKey === key)}
            >
              {navIcon(key, Icon)}
              <span>
                {label(key)}
                {countText(key)}
              </span>
            </Button>
          ))}
          <Button
            onClick={() => setMobileOpen(true)}
            aria-expanded={mobileOpen}
            aria-controls={drawerId}
            sx={barButtonSx(activeKey === 'jobs' || activeKey === 'profile')}
          >
            <MoreHorizIcon />
            <span>{label('more')}</span>
          </Button>
        </Box>
      )}
    </Box>
  );
}
